// Shift types — barn config for what a shift can be.
//
// The sibling of Lesson Types, with one difference that changes the form: a
// lesson type has a LENGTH, a shift type has HOURS. Morning feed is not "a
// three-hour block"; it starts at six. Storing a length would mean every new
// shift re-answered a question the barn settled long ago, which is the whole
// point of having a type.
//
// Hours are optional. "Cover" has no natural ones, and a type without them just
// means the shift asks — it doesn't mean the type is unfinished.
//
// Retiring is deliberately not deleting. A type that has been used names a
// shift in the past, so it stays readable on old weeks and stops being offered
// on new ones. The row says how many shifts carry it, so that is an informed
// choice rather than a guess.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { COLOR_TOKENS, colorClass, colorLabel } from '../../lib/lessonTypes';
import { IconButton, PencilIcon } from '../../components/ui/icons';
import TimeSelect from '../../components/ui/TimeSelect';
import { timeRange, minutesBetween, formatMinutes, shiftsLabel } from '../../lib/shifts';

export default function ShiftTypesPage() {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [retiring, setRetiring] = useState(null); // the type being confirmed
  const [err, setErr] = useState('');

  const typesQ = useQuery({
    queryKey: ['shift-types', 'all'],
    queryFn: () => api.get('/shift-types?all=1'),
    enabled: canEdit,
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['shift-types'] });
    // The roster embeds the type list and every shift's color, so it has to be
    // refetched or a renamed type keeps its old name on screen.
    qc.invalidateQueries({ queryKey: ['shifts'] });
  };

  const save = useMutation({
    mutationFn: ({ id, ...fields }) =>
      id ? api.patch(`/shift-types/${id}`, fields) : api.post('/shift-types', fields),
    onSuccess: () => { setAdding(false); setEditingId(null); setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const retire = useMutation({
    mutationFn: (id) => api.del(`/shift-types/${id}`),
    onSuccess: () => { setRetiring(null); setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const restore = useMutation({
    mutationFn: (id) => api.patch(`/shift-types/${id}`, { active: true }),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  const reorder = useMutation({
    mutationFn: ({ id, sortOrder }) => api.patch(`/shift-types/${id}`, { sortOrder }),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canEdit) {
    return (
      <div className="max-w-3xl mx-auto p-4">
        <div className="card p-8 text-center text-ink-500">
          Shift types are set by an owner or manager.
        </div>
      </div>
    );
  }

  const all = typesQ.data?.shiftTypes ?? [];
  const active = all.filter((t) => t.active);
  const retired = all.filter((t) => !t.active);

  /**
   * Swap a row with its neighbour.
   *
   * Two PATCHes rather than a drag handle: the list is six rows long and the
   * order only decides what the picker offers first. @dnd-kit is already in the
   * bundle and unused, and pulling it in for this would cost more than it buys.
   */
  function move(index, delta) {
    const a = active[index];
    const b = active[index + delta];
    if (!a || !b) return;
    reorder.mutate({ id: a.id, sortOrder: b.sortOrder });
    reorder.mutate({ id: b.id, sortOrder: a.sortOrder });
  }

  return (
    <div className="max-w-3xl mx-auto p-4 pb-24 sm:pb-8">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">Shift Types</h1>
          <p className="text-sm text-ink-500">
            The kinds of work the barn runs, their usual hours, and their color on the roster.
          </p>
        </div>
        {!adding && (
          <button onClick={() => { setAdding(true); setEditingId(null); }} className="btn-primary shrink-0">
            + Add Type
          </button>
        )}
      </header>

      {err && <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 mb-3">{err}</p>}

      {adding && (
        <TypeForm
          onCancel={() => setAdding(false)}
          onSave={(fields) => save.mutate(fields)}
          saving={save.isPending}
        />
      )}

      {typesQ.isLoading ? (
        <div className="card p-8 text-center text-ink-400">Loading…</div>
      ) : (
        <ul className="card divide-y divide-ink-100">
          {active.map((t, i) =>
            editingId === t.id ? (
              <li key={t.id} className="p-3">
                <TypeForm
                  initial={t}
                  onCancel={() => setEditingId(null)}
                  onSave={(fields) => save.mutate({ id: t.id, ...fields })}
                  saving={save.isPending}
                />
              </li>
            ) : retiring === t.id ? (
              <li key={t.id} className="p-4 bg-ink-50">
                <p className="text-sm text-ink-700">
                  Retire <strong>{t.name}</strong>? It stops being offered on new shifts.{' '}
                  {t.inUse > 0 ? (
                    <>
                      The {shiftsLabel(t.inUse)} already using it keep the name and their hours — nothing on
                      the roster changes.
                    </>
                  ) : (
                    'Nothing is using it.'
                  )}
                </p>
                <div className="mt-3 flex gap-2 justify-end">
                  <button className="btn btn-secondary" onClick={() => setRetiring(null)}>
                    Keep it
                  </button>
                  <button
                    className="btn bg-rose-600 text-white hover:bg-rose-700"
                    disabled={retire.isPending}
                    onClick={() => retire.mutate(t.id)}
                  >
                    Retire
                  </button>
                </div>
              </li>
            ) : (
              <TypeRow
                key={t.id}
                t={t}
                first={i === 0}
                last={i === active.length - 1}
                busy={save.isPending || retire.isPending || reorder.isPending}
                onUp={() => move(i, -1)}
                onDown={() => move(i, 1)}
                onEdit={() => { setAdding(false); setEditingId(t.id); }}
                onRetire={() => setRetiring(t.id)}
              />
            ),
          )}
          {active.length === 0 && (
            <li className="p-6 text-center text-sm text-ink-400">
              No shift types. Add one and it becomes an option when you build the roster.
            </li>
          )}
        </ul>
      )}

      {retired.length > 0 && (
        <section className="mt-6">
          <h2 className="text-sm font-medium text-ink-500 mb-2">Retired</h2>
          <ul className="card divide-y divide-ink-100">
            {retired.map((t) => (
              <li key={t.id} className="p-3 flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 min-w-0">
                  <span className={'w-2.5 h-2.5 rounded-full shrink-0 ' + colorClass(t.color, 'dot')} />
                  <span className="text-ink-500 truncate">{t.name}</span>
                  {t.inUse > 0 && (
                    <span className="text-xs text-ink-400 shrink-0">still on {shiftsLabel(t.inUse)}</span>
                  )}
                </span>
                <button
                  onClick={() => restore.mutate(t.id)}
                  disabled={restore.isPending}
                  className="text-sm text-ink-600 hover:text-ink-900 underline shrink-0"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-6 text-xs text-ink-400">
        The order here is the order they are offered when you add a shift.
      </p>
    </div>
  );
}

function TypeRow({ t, first, last, busy, onUp, onDown, onEdit, onRetire }) {
  const mins = t.startTime && t.endTime ? minutesBetween(t.startTime, t.endTime) : 0;

  return (
    <li className="p-3 flex flex-wrap items-center gap-3">
      <span className="flex flex-col shrink-0">
        <ArrowButton dir="up" onClick={onUp} disabled={busy || first} label={`Move ${t.name} up`} />
        <ArrowButton dir="down" onClick={onDown} disabled={busy || last} label={`Move ${t.name} down`} />
      </span>
      <span className="flex items-center gap-2 min-w-0 flex-1">
        <span className={'w-3 h-3 rounded-full shrink-0 ' + colorClass(t.color, 'dot')} />
        <span className="min-w-0">
          <span className="font-medium text-ink-900 block truncate">{t.name}</span>
          <span className="text-sm text-ink-500">
            {/* A type with no hours is a real answer, not a blank field — the
                shift asks instead. Saying so beats an empty line. */}
            {mins
              ? `${timeRange(t.startTime, t.endTime)} · ${formatMinutes(mins)}`
              : 'No usual hours'}
            {t.inUse > 0 ? ` · ${shiftsLabel(t.inUse)}` : ''}
          </span>
        </span>
      </span>
      <span className="flex items-center gap-3 shrink-0">
        <IconButton icon={PencilIcon} label={`Edit ${t.name}`} onClick={onEdit} disabled={busy} />
        <button
          onClick={onRetire}
          disabled={busy}
          className="text-sm text-ink-500 hover:text-rose-700 px-1"
          aria-label={`Retire ${t.name}`}
        >
          Retire
        </button>
      </span>
    </li>
  );
}

function ArrowButton({ dir, onClick, disabled, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="w-6 h-5 flex items-center justify-center text-ink-300 hover:text-ink-700 disabled:opacity-30 disabled:hover:text-ink-300"
    >
      <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d={dir === 'up' ? 'M4.5 12.5 10 7l5.5 5.5' : 'M4.5 7.5 10 13l5.5-5.5'}
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function TypeForm({ initial, onCancel, onSave, saving }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [color, setColor] = useState(initial?.color ?? 'slate');
  // Whether this type has usual hours at all, kept separate from the times so
  // turning it off and on again doesn't lose what they were.
  const [hasHours, setHasHours] = useState(initial ? !!initial.startTime : true);
  const [start, setStart] = useState(initial?.startTime || '06:00');
  const [end, setEnd] = useState(initial?.endTime || '09:00');

  const mins = minutesBetween(start, end);
  const backwards = hasHours && mins === 0;
  const canSave = name.trim() && !saving && !backwards;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSave) return;
        onSave({
          name: name.trim(),
          color,
          startTime: hasHours ? start : null,
          endTime: hasHours ? end : null,
        });
      }}
      className={initial ? 'space-y-3' : 'card p-4 mb-4 space-y-3'}
    >
      <div>
        <label htmlFor="st-name" className="block text-sm font-medium text-ink-700 mb-1">Name</label>
        <input
          id="st-name"
          className="input"
          placeholder="e.g. Morning feed"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </div>

      <fieldset>
        <legend className="block text-sm font-medium text-ink-700 mb-1">Usual hours</legend>
        <div className="flex gap-2 mb-2">
          {[
            [true, 'Set hours'],
            [false, 'Ask each time'],
          ].map(([value, label]) => (
            <button
              key={label}
              type="button"
              onClick={() => setHasHours(value)}
              aria-pressed={hasHours === value}
              className={
                'px-3 py-1.5 rounded-full text-sm border ' +
                (hasHours === value
                  ? 'bg-hunter text-white border-hunter'
                  : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300')
              }
            >
              {label}
            </button>
          ))}
        </div>
        {hasHours ? (
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="st-start">Starts</label>
              <TimeSelect label="Starts" value={start} onChange={setStart} id="st-start" />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="st-end">Ends</label>
              <TimeSelect label="Ends" value={end} onChange={setEnd} id="st-end" />
            </div>
            <p className="text-sm text-ink-500 pb-2">
              {backwards ? (
                <span className="text-rose-600">End has to be after start.</span>
              ) : (
                formatMinutes(mins)
              )}
            </p>
          </div>
        ) : (
          <p className="text-xs text-ink-400">
            A new shift of this kind starts blank and you type the hours then.
          </p>
        )}
      </fieldset>

      <div>
        <span className="block text-sm font-medium text-ink-700 mb-1">Color on the roster</span>
        <div className="flex flex-wrap gap-2">
          {COLOR_TOKENS.map((tok) => (
            <button
              key={tok}
              type="button"
              onClick={() => setColor(tok)}
              aria-label={colorLabel(tok)}
              aria-pressed={color === tok}
              title={colorLabel(tok)}
              className={
                'w-8 h-8 rounded-full ' +
                colorClass(tok, 'dot') +
                (color === tok ? ' ring-2 ring-offset-2 ring-ink-800' : '')
              }
            />
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={!canSave} className="btn-primary">
          {saving ? 'Saving…' : initial ? 'Save Changes' : 'Add Type'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-ink-500 hover:text-ink-800 px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

function friendly(e) {
  const code = e?.body?.error || e?.message;
  switch (code) {
    case 'name_taken':
      return 'A shift type with that name already exists.';
    case 'missing_name':
      return 'Give the type a name.';
    case 'invalid_color':
      return 'Pick a color from the list.';
    case 'partial_time_range':
      return 'Set both a start and an end, or neither.';
    case 'end_before_start':
      return 'The end is before the start.';
    case 'invalid_time':
      return "That didn't read as a time.";
    case 'not_found':
      return 'That type is already gone.';
    case 'forbidden':
    case 'not_allowed':
      return 'Only an owner or manager can change shift types.';
    default: {
      if (!code && !e?.status) return 'No response from the server. Check your connection.';
      return `That didn't save — ${code || 'no reason given'}${e?.status ? ` (${e.status})` : ''}.`;
    }
  }
}
